import axios from "axios";

type ApiErrorBody = {
  message?: string;
  error?: string;
  data?: { message?: string };
};

/** Extracts the API `message` field from auth/error responses. */
export const getApiErrorMessage = (
  error: unknown,
  fallback = "",
): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorBody | undefined;
    return (
      data?.message ||
      data?.data?.message ||
      data?.error ||
      error.message ||
      fallback
    );
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

/**
 * Checks if the error is an HTTP 402 (Payment Required) response.
 * Used for subscription limit handling - show upgrade prompt.
 */
export const is402Error = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 402;
};

/**
 * Why a 402 was returned, when the gateway says. "trial_excluded" means the plan includes the
 * feature but it is locked during the free trial (docs/free-trial-design.md §15).
 */
export const getPaywallReason = (error: unknown): string | null => {
  if (!is402Error(error)) return null;
  const data = (error as { response?: { data?: { data?: { reason?: unknown } } } })
    .response?.data?.data;
  return typeof data?.reason === "string" && data.reason ? data.reason : null;
};

export const isTrialExcludedError = (error: unknown): boolean =>
  getPaywallReason(error) === "trial_excluded";

/** True when an axios error carries the given HTTP status. */
export const hasHttpStatus = (error: unknown, status: number): boolean =>
  axios.isAxiosError(error) &&
  (error.response?.status === status || error.status === status);
